import axios from 'axios';
import bookingModal from '../modal';
import cryptDecrypt from '../../../core/crypt-decrypt';

declare let booking_object: any;

const modCryptDecrypt = new cryptDecrypt("25a02a871c482b08a36db790703c455c03e8e38d37d9615ba4b98be07f7a6842");

// Interface for Booking Data
interface BookingData {
    order_uuid: string;
    room_id: number;
    room_name: string;
    game_name: string;
    arena_id: number;
    type_arena: string;
    mode: string;
    game_id: number;
    date_start: string;
    date_end: string;
    players: number;
    price_paid: string;
    price_left: string;
    price_total: string;
    time: string;
    slot_time: string;
    day: string;
    name: string;
    surname: string;
    email: string;
    phone_prefix_id: number;
    phone: string;
    phone_full: string;
}

export default class BookingStepTwo{
    
    private callRestApi: string;
    private output: boolean;

    constructor(){
        
        this.callRestApi = booking_object.rest_urls.step_two;
        this.output = false;

    }

    /**
     * Sends booking data via API
     */
    async sendData(nextButtonSelector: string, formSelector: string): Promise<boolean>{
        
        const btn = document.querySelector<HTMLButtonElement>(nextButtonSelector);
        const form = document.querySelector<HTMLFormElement>(formSelector);

        if(!form) throw new Error("Form not found");

        const spin = this.createSpinner();
        btn?.append(spin);

        const formData = new FormData(form);
        const orderData = this.getLocalStorage();
        formData.append("order_id", orderData?.order_uuid || '');

        try{
            
            const response = await axios.post(this.callRestApi, new URLSearchParams({ 'fields': new URLSearchParams(formData as any).toString() }).toString(), {
                validateStatus: status => status === 200
            });

            if(response.data.status){
                
                this.storeLocalStorage(response.data.order_data);
                this.updateUI();
                this.output = true;

            }else{
                
                this.displayError(response.data.message);
                this.output = false;

            }

        }catch(error: any){
            
            if(error.response?.status === 201){
                
                this.displayError(error.response.data.message);

            }

            this.output = false;

        }finally{
            
            spin.remove();

        }

        return this.output;
    }

    /**
     * Creates a loading spinner icon
     */
    private createSpinner(): HTMLElement{
        
        const spin = document.createElement('i');
        spin.id = 'bgdrop-spin';
        spin.className = 'fa-solid fa-circle-notch fa-spin ml-1 mt-1';
        return spin;

    }

    /**
     * Stores booking data securely in local storage
     */
    private storeLocalStorage(data: Partial<BookingData>): void{
        
        const existingData = this.getLocalStorage() || {};
        const updatedData: BookingData = { ...existingData, ...data } as BookingData;
        const encrypted = modCryptDecrypt.encryptObject(updatedData);
        localStorage.setItem('booking-data-order', JSON.stringify(encrypted));

    }

    /**
     * Retrieves decrypted booking data from local storage
     */
    private getLocalStorage(): BookingData | null {
        
        try {
            
            const storedData = localStorage.getItem('booking-data-order');

            if(!storedData) return null;

            const { encryptedData, iv } = JSON.parse(storedData);

            return modCryptDecrypt.decryptObject<BookingData>(encryptedData, iv);

        }catch(error){
            
            // console.error("Error retrieving booking data:", error);
            return null;

        }

    }

    /**
     * Updates UI elements with booking data
     */
    private updateUI(): void{

        const bookingData = this.getLocalStorage();

        if(!bookingData) return;

        const fieldMappings: Record<keyof BookingData, string> = {
            room_name: "dl-room",
            game_name: "dl-game",
            mode: "dl-mode",
            players: "dl-players",
            price_paid: "dl-paid",
            price_left: "dl-left",
            price_total: "dl-total",
            slot_time: "dl-time",
            day: "dl-date",
            name: "dl-name",
            surname: "dl-surname",
            email: "dl-email",
            phone_full: "dl-phone",
            room_id: "",
            type_arena: "",
            date_start: "",
            date_end: "",
            time: "",
            order_uuid: "",
            arena_id: "",
            game_id: "",
            phone_prefix_id: "",
            phone: ""
        };

        Object.entries(fieldMappings).forEach(([key, elementId]) => {

            if(elementId) this.updateElementText(elementId, bookingData[key as keyof BookingData]);

        });

    }

    /**
     * Updates text content of an HTML element
     */
    private updateElementText(elementId: string, text: string | number): void{
        
        const element = document.getElementById(elementId);
        if(element) element.innerHTML = String(text);

    }

    /**
     * Displays an error message in a modal
     */
    private displayError(message: string): void{
        
        document.body?.insertAdjacentHTML('beforeend', message);
        bookingModal('errorModal', 'closeErrorModal');

    }

}
